/**
 * Pure proposal builder for the Settings page "Backlog ユーザーを自動マッチ"
 * flow. Extracted from the component so the 1:1 / skip-already-linked /
 * fuzzy-match rules are unit-testable without React or Firestore.
 *
 * The fuzzy name matching itself lives in `@mct/shared`
 * (`matchBacklogNameToAssignee`) so the web proposal and the Functions
 * backfill share one definition.
 */
import { matchBacklogNameToAssignee, type AssigneeNameMatchType } from '@mct/shared';

export interface AutoMatchAssignee {
  assigneeId: string;
  name: string;
}

export interface AutoMatchBacklogUser {
  id: number;
  name: string;
}

export interface BacklogAutoMatchProposal {
  assigneeId: string;
  assigneeName: string;
  backlogUserId: number;
  backlogUserName: string;
  matchType: AssigneeNameMatchType;
}

export interface ProposeBacklogAutoMatchesInput {
  assignees: readonly AutoMatchAssignee[];
  backlogUsers: readonly AutoMatchBacklogUser[];
  /** Assignee ids that already carry a Backlog link — never re-proposed. */
  linkedAssigneeIds: ReadonlySet<string>;
  /** Backlog user ids already linked to some assignee — never re-proposed. */
  linkedBacklogUserIds: ReadonlySet<number>;
}

/**
 * Build the auto-match proposal list.
 *
 * Rules:
 *   - only unlinked assignees are eligible targets;
 *   - already-linked Backlog users are skipped (1:1 from the Backlog side);
 *   - each assignee is proposed at most once — the FIRST Backlog user that
 *     fuzzy-matches it wins (iteration order of `backlogUsers`), so a later
 *     user can't steal an assignee already claimed in this run.
 */
export function proposeBacklogAutoMatches(
  input: ProposeBacklogAutoMatchesInput,
): BacklogAutoMatchProposal[] {
  const eligible = input.assignees.filter(
    (a) => !input.linkedAssigneeIds.has(a.assigneeId),
  );
  const candidates = eligible.map((a) => ({ id: a.assigneeId, name: a.name }));
  const nameById = new Map(eligible.map((a) => [a.assigneeId, a.name]));

  const proposals: BacklogAutoMatchProposal[] = [];
  const claimedAssignees = new Set<string>();
  for (const bu of input.backlogUsers) {
    if (input.linkedBacklogUserIds.has(bu.id)) continue;
    const match = matchBacklogNameToAssignee(bu.name, candidates);
    if (!match) continue;
    if (claimedAssignees.has(match.id)) continue;
    claimedAssignees.add(match.id);
    proposals.push({
      assigneeId: match.id,
      assigneeName: nameById.get(match.id) ?? match.id,
      backlogUserId: bu.id,
      backlogUserName: bu.name,
      matchType: match.matchType,
    });
  }
  return proposals;
}
